import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { AuthenticationService } from '../guards/authentication.service';
import { FramedService } from '../framed-service';
import { SecurityApiService } from './security-api.service';

@Component({
    selector: 'backup-authenticator-prompt',
    standalone: true,
    imports: [CommonModule],
    template: `
        <aside *ngIf="visible" class="backup-prompt" aria-labelledby="backup-method-title" aria-live="polite">
            <h2 id="backup-method-title">Add a backup sign-in method</h2>
            <p>Set up an authenticator app so you can access your account if your passkey isn’t available.</p>
            <div class="actions">
                <button class="setup" type="button" [disabled]="openingSettings" (click)="setUpAuthenticator()">Set up authenticator</button>
                <button class="later" type="button" [disabled]="openingSettings" (click)="later()">Later</button>
            </div>
            <p *ngIf="navigationError" role="alert">{{ navigationError }}</p>
        </aside>
    `,
    styles: [`
        .backup-prompt { position: fixed; right: 24px; bottom: 24px; z-index: 1000;
            width: 390px; max-width: calc(100vw - 32px); box-sizing: border-box; padding: 22px;
            border: 1px solid #d8cfee; border-radius: 16px; background: #fff; color: #292331;
            box-shadow: 0 8px 32px rgba(28, 16, 48, .18); }
        h2 { font-size: 18px; line-height: 1.4; margin: 0 0 8px; font-weight: 600; }
        p { font-size: 14px; line-height: 1.6; margin: 0 0 18px; }
        .actions { display: flex; gap: 12px; flex-wrap: wrap; }
        button { min-height: 44px; padding: 10px 16px; border-radius: 8px; font: inherit;
            font-size: 14px; cursor: pointer; }
        .setup { background: #6d43b7; color: #fff; border: 1px solid #6d43b7; }
        .later { background: transparent; color: #4b3b60; border: 1px solid #c8bfd6; }
        button:focus-visible { outline: 3px solid #9973db; outline-offset: 3px; }
        button:disabled { opacity: .6; cursor: wait; }
        @media (max-width: 480px) { .backup-prompt { right: 16px; bottom: 16px; } }
    `],
})
export class BackupAuthenticatorPromptComponent implements OnInit, OnDestroy {
    openingSettings = false;
    navigationError = '';
    private eligible = false;
    private eligibleUserId = '';
    private subscriptions = new Subscription();
    private checkedUsers = new Set<string>();
    private generation = 0;
    private readonly snoozeDuration = 7 * 24 * 60 * 60 * 1000;

    constructor(
        private auth: AuthenticationService,
        private securityApi: SecurityApiService,
        private router: Router,
        private framed: FramedService
    ) {}

    get visible(): boolean {
        return this.eligible && this.eligibleUserId === this.userId()
            && this.canShowOnRoute() && this.auth.isAuthenticated$.value;
    }

    ngOnInit(): void {
        this.subscriptions.add(this.auth.isAuthenticated$.subscribe((authenticated) => {
            if (!authenticated) {
                this.generation++;
                this.eligible = false;
                this.checkedUsers.clear();
            } else {
                void this.checkAsync();
            }
        }));
        this.subscriptions.add(this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) void this.checkAsync();
        }));
    }

    later(): void {
        this.eligible = false;
        const id = this.userId();
        if (!id) return;
        try { localStorage.setItem(this.snoozeKey(id), String(Date.now() + this.snoozeDuration)); }
        catch { /* The reminder stays dismissed for this session when storage is unavailable. */ }
    }

    async setUpAuthenticator(): Promise<void> {
        if (this.openingSettings) return;
        this.openingSettings = true;
        this.navigationError = '';
        try {
            if (await this.router.navigate(['/dashboard', { outlets: { panel: ['settings'] } }],
                { queryParams: { setupAuthenticator: 'true' } })) this.later();
            else this.navigationError = 'Settings could not be opened. Please try again.';
        } catch {
            this.navigationError = 'Settings could not be opened. Please try again.';
        } finally {
            this.openingSettings = false;
        }
    }

    ngOnDestroy(): void {
        this.generation++;
        this.eligible = false;
        this.subscriptions.unsubscribe();
    }

    private async checkAsync(): Promise<void> {
        const id = this.userId();
        if (!id || !this.auth.isAuthenticated$.value || !this.canShowOnRoute() || this.checkedUsers.has(id)) return;
        this.checkedUsers.add(id);
        this.eligible = false;
        try {
            const snoozedUntil = Number(localStorage.getItem(this.snoozeKey(id)));
            if (Number.isFinite(snoozedUntil) && snoozedUntil > Date.now()) return;
        } catch { /* Credential lookup remains optional when browser storage is unavailable. */ }
        const generation = this.generation;
        try {
            const credentials = await firstValueFrom(this.securityApi.getCredentials$());
            if (generation !== this.generation || this.userId() !== id || !this.auth.isAuthenticated$.value) return;
            this.eligible = !credentials.hasTotp && credentials.passkeys.length > 0;
            this.eligibleUserId = id;
        } catch { /* An optional reminder must never prevent sign-in or trigger retries. */ }
    }

    private canShowOnRoute(): boolean {
        return /^\/dashboard(?:[\/(?;]|$)/.test(this.router.url)
            && !this.router.url.includes('panel:settings') && !this.framed.isFramed();
    }
    private userId(): string { return this.auth.user?._id ? String(this.auth.user._id) : ''; }
    private snoozeKey(id: string): string { return `backup-authenticator-snooze:${id}`; }
}
